import React, { useRef, useState, useEffect } from "react";
import { Formik, Form } from "formik";
import {
    InputGroup,
    Label,
    Wrapper,
    ScrollableContent,
    ModalHeader,
} from "../../Styles/InputStyles";
import { MdClose, MdChevronRight, MdAdd } from "react-icons/md";
import tw from "tailwind-styled-components";
import "react-datepicker/dist/react-datepicker.css";
import * as Yup from "yup";
import { ProfileSection, UserInfo } from "../../Styles/PageStyles";
import { CircularProgress } from "@mui/material";
import { GoSearch } from "react-icons/go";
import { capitalizeFirstLetter } from "../../../helpers";

const SelectLeaseFlat = ({
    handleClose,
    selectedData,
    mutateFetchLandlordLeaseDetailsFunction,
    isLandlordLeaseDetailsLoading,
    setSelectedFlatForLease
}) => {
    let formikRef = useRef(null);
    const [height, setHeight] = useState(window.innerHeight);
    const [searchText, setSearchText] = useState("");
    const [loadingFlatId, setLoadingFlatId] = useState(null);

    useEffect(() => {
        if (formikRef.current) {
            formikRef.current.resetForm();
        }
        const handleResize = () => {
            setHeight(window.innerHeight);
        };
        window.addEventListener("resize", handleResize);
        handleResize();
        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, []);

    useEffect(() => {
        if (!isLandlordLeaseDetailsLoading && loadingFlatId) {
            setLoadingFlatId(null);
        }
    }, [isLandlordLeaseDetailsLoading]);

    const filteredFlats = selectedData?.flats?.filter(flat => flat.id !== 0) || [];

    // Filter flats based on search
    // const searchedFlats = filteredFlats.filter(flat =>
    //     flat.name?.toLowerCase().includes(searchText.toLowerCase())
    // );
    const searchedFlats = filteredFlats.filter(flat => {
        const flatName = flat.name?.toLowerCase() || "";
        const displayName = `flat ${flatName}`;
        const searchTerm = searchText.toLowerCase();

        return flatName.includes(searchTerm) || displayName.includes(searchTerm);
    });

    const handleFlatSelect = (flat) => {
        if (flat && !loadingFlatId) {
            setLoadingFlatId(flat.id);

            if (formikRef.current) {
                formikRef.current.setFieldValue("flatId", flat.id);
            }
            if (setSelectedFlatForLease) {
                setSelectedFlatForLease({
                    id: flat.id,
                    name: flat.name,
                    isLeaseAdded: flat.isLeaseAdded
                });
            }
            mutateFetchLandlordLeaseDetailsFunction({
                propId: selectedData.id,
                flatId: flat.id
            });
        }
    };

    const initialValues = {
        flatId: null,
    };

    const validationSchema = Yup.object({
        flatId: Yup.number()
            .required('Please select a flat')
            .nullable(),
    });

    const renderFlatItem = (flat) => {
        const isLeaseAdded = flat.isLeaseAdded === true;
        const isLoading = loadingFlatId === flat.id;

        return (
            <FlatRow
                key={flat.id}
                onClick={() => !isLoading && handleFlatSelect(flat)}
                isLeaseAdded={isLeaseAdded}
                isLoading={isLoading}
            >
                <FlatInfo>
                    <FlatName>Flat {capitalizeFirstLetter(flat.name)}</FlatName>
                </FlatInfo>

                {isLoading ? (
                    <ActionWrapper>
                        <CircularProgress size={16} thickness={4} />
                    </ActionWrapper>
                ) :
                    isLeaseAdded ? (
                        <ActionWrapper>

                            {/* <span>View</span> */}
                            <MdChevronRight size={20} />
                        </ActionWrapper>
                    ) : (
                        <ActionWrapper>
                            <MdAdd size={18} />
                            {/* <span>Add Lease</span> */}
                        </ActionWrapper>
                    )}
            </FlatRow>
        );
    };

    return (
        <Container style={{ height: `${height}px`, WebkitOverflowScrolling: 'touch' }}>
            <ModalHeader>
                <h1 className="text-white text-lg font-medium">Select Flat for Lease</h1>
                <button onClick={handleClose}>
                    <MdClose color="white" size={24} />
                </button>
            </ModalHeader>

            <ProfileSection className="flex justify-between">
                <UserInfo>
                    <span className="font-medium mb-0">
                        {selectedData?.name || "--"}
                    </span>
                    <span className="text-sm text-gray-600">
                        {selectedData?.streetAddress || "--"}
                    </span>
                </UserInfo>
            </ProfileSection>

            <div className="px-6 h-full flex flex-col">
                {/* Search Box */}
                <div className="py-4 relative">
                    <div className="relative">
                        <input
                            type="text"
                            placeholder="Search flat..."
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            className="w-full px-8 h-12 py-2 border border-gray-200 rounded-md focus:outline-none focus:border-primary-color text-gray-800 placeholder-gray-300"
                        />
                        <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                            <GoSearch className="w-4 h-4 text-gray-400" />
                        </div>
                    </div>
                </div>

                <Formik
                    innerRef={formikRef}
                    initialValues={initialValues}
                    validationSchema={validationSchema}
                    validateOnChange={true}
                    validateOnBlur={true}
                >
                    {() => (
                        <Form className="flex-1 flex flex-col">
                            <ScrollableContent className="flex-1 max-h-[calc(100vh-100px)]">
                                <Wrapper className="!mt-0">
                                    <InputGroup>
                                        {/* <Label>Select Flat *</Label> */}

                                        {searchedFlats.length === 0 ? (
                                            <EmptyState>
                                                {filteredFlats.length === 0 ? "No flats available" : "No flats found"}
                                            </EmptyState>
                                        ) : (
                                            <FlatsList>
                                                {searchedFlats.map(flat => renderFlatItem(flat))}
                                            </FlatsList>
                                        )}
                                    </InputGroup>
                                </Wrapper>
                            </ScrollableContent>
                        </Form>
                    )}
                </Formik>
            </div>
        </Container>
    );
};

// Styled Components
const Container = tw.div`bg-white w-full h-full min-h-[100vh] flex flex-col`;
const FlatsList = tw.div`space-y-3 mt-2 pb-28`;
const FlatRow = tw.div`
    flex items-center justify-between 
    p-4 border rounded-lg 
    transition-all duration-200
    ${props => props.isLoading
        ? 'bg-gray-100 border-gray-300 opacity-70'
        : 'bg-white border-primary-color/30 cursor-pointer hover:border-primary-color hover:shadow-sm'
    }
`;
const FlatInfo = tw.div`flex flex-col gap-1`;
const FlatName = tw.span`text-base font-medium text-gray-600`;
const FlatMeta = tw.span`text-xs text-gray-400`;
const EmptyState = tw.div`text-center py-8 text-gray-500 `;

const ActionWrapper = tw.div`
  flex items-center gap-1.5
  text-xs font-medium px-2.5 py-1.5
  rounded-lg transition-all
  bg-gray-100 text-gray-700 group-hover:bg-gray-200
  `;

export default SelectLeaseFlat;